import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { getToneTokens } from "../../styles/shared";

export const CalendarShell = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  overflow: hidden;
`;

export const CalendarHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};

  @media (max-width: 720px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

export const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  line-height: 1.2;
  letter-spacing: -0.01em;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 720px) {
    flex: 1 1 100%;
    order: -1;
  }
`;

export const HeaderControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
`;

export const ScaleGroup = styled.div`
  display: inline-flex;
  align-items: stretch;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
`;

export const ScaleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  min-height: 32px;
  padding: 0 12px;
  border: 0;
  background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : "transparent")};
  color: ${({ theme, $active }) => ($active ? theme.textPrimary : theme.textSecondary)};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  & + & {
    border-left: 1px solid ${({ theme }) => theme.border};
  }

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textPrimary};
  }

  &[aria-pressed="true"] {
    color: ${({ theme }) => theme.accent};
  }
`;

export const NavGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
`;

export const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  padding: 0 10px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  & + & {
    border-left: 1px solid ${({ theme }) => theme.border};
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:disabled {
    color: ${({ theme }) => theme.textSecondary};
    opacity: 0.5;
    cursor: default;
  }
`;

/* ============================================================
 *  Mes
 * ============================================================ */

export const WeekdayRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: ${({ theme }) => theme.surfaceAlt};
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

export const WeekdayCell = styled.div`
  padding: 8px 12px;
  /* Mockup: rótulo do dia da semana alinhado à esquerda. */
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};

  & + & {
    border-left: 1px solid ${({ theme }) => theme.border};
  }
`;

export const MotionView = styled(motion.div)`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, minmax(96px, 1fr));
  min-height: 576px;
  background: ${({ theme }) => theme.surface};
`;

export const DayCell = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 6px 6px 8px;
  border-top: 1px solid ${({ theme }) => theme.border};
  border-left: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  &:nth-child(7n + 1) {
    border-left: 0;
  }
  &:nth-child(-n + 7) {
    border-top: 0;
  }

  ${({ $outside }) =>
    $outside &&
    css`
      background: transparent;
    `}

  ${({ $weekend, theme }) =>
    $weekend &&
    css`
      background: ${theme.surfaceAlt};
    `}

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const DayNumberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  min-height: 22px;
`;

export const DayNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: ${({ $today }) => ($today ? 600 : 500)};
  line-height: 1;

  ${({ $today, theme }) =>
    $today
      ? css`
          background: ${theme.accent};
          color: #ffffff;
        `
      : css`
          color: ${theme.textPrimary};
        `}

  ${({ $outside, $today, theme }) =>
    $outside &&
    !$today &&
    css`
      color: ${theme.textSecondary};
      opacity: 0.45;
    `}
`;

export const DayEvents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

/* Feriado escrito como "compromisso" do dia — visualmente distinto dos
 * ambientes programados (cor de acento + borda, em vez das tones de status). */
export const FeriadoChip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.2;
  padding: 2px 6px;
  border-radius: 4px;
  border-left: 3px solid ${({ theme }) => theme.accent};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.accent};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const EventPill = styled.button`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      border-left: 3px solid ${tokens.border};
    `;
  }}

  display: flex;
  align-items: center;
  width: 100%;
  min-height: 18px;
  padding: 2px 6px;
  border-radius: 3px;
  border-top: 0;
  border-right: 0;
  border-bottom: 0;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.25;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ $outside }) =>
    $outside &&
    css`
      opacity: 0.55;
    `}

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  }
`;

export const MoreLink = styled.button`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  margin-top: 1px;
  padding: 2px 6px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 3px;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const EmptyMessage = styled.div`
  padding: 32px 24px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
`;

/* ============================================================
 *  Eventos agrupados por cliente (F11) — cliente uma vez +
 *  ambientes em sub-lista, dentro de cada dia.
 * ============================================================ */

export const GrupoCliente = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export const GrupoClienteNome = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  padding: 1px 2px;
  /* Mockup: nome do cliente em UPPERCASE, pequeno e discreto (secondary). */
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};

  ${({ $outside }) =>
    $outside &&
    css`
      opacity: 0.5;
    `}

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const GrupoClienteCont = styled.span`
  /* Mockup: contador simples ao lado do nome, sem pill. */
  flex: none;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.textTertiary};
`;

export const AmbienteItem = styled.button`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      border-left: 3px solid ${tokens.border};
    `;
  }}

  display: flex;
  align-items: center;
  /* Mockup: card ocupa a largura toda sob o cliente (sem indent). */
  width: 100%;
  min-height: 20px;
  padding: 3px 8px;
  border-radius: 6px;
  border-top: 0;
  border-right: 0;
  border-bottom: 0;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.25;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ $outside }) =>
    $outside &&
    css`
      opacity: 0.55;
    `}

  /* Previsao (ambiente ainda sem lote): contorno tracejado e sem
     preenchimento — o tom continua dizendo o prazo; o tracejado diz que
     ainda nao ha lote. */
  ${({ theme, $tone = "default", $previsao }) =>
    $previsao &&
    css`
      background: transparent;
      border: 1px dashed ${getToneTokens(theme, $tone).border};
      border-left-width: 3px;
    `}

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  }
`;

/* ============================================================
 *  Semana
 * ============================================================ */

export const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  min-height: 520px;
  background: ${({ theme }) => theme.surface};
`;

export const WeekColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};

  &:first-child {
    border-left: 0;
  }

  ${({ $weekend, theme }) =>
    $weekend &&
    css`
      background: ${theme.surfaceAlt};
    `}
`;

export const WeekColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  /* Mockup: header de coluna limpo (sem preenchimento). */
  background: transparent;
`;

export const WeekWeekdayLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};
`;

export const WeekColumnBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  overflow: auto;
  min-height: 0;
  flex: 1 1 auto;
`;

/* ============================================================
 *  Dia — layout 2 colunas (mockup): painel de data à esquerda,
 *  lista de ambientes agrupada por cliente à direita.
 * ============================================================ */

export const DayLayout = styled.div`
  display: grid;
  grid-template-columns: 190px 1fr;
  min-height: 460px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const DayPanel = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 18px 16px;
  border-right: 1px solid ${({ theme }) => theme.border};

  @media (max-width: 640px) {
    border-right: 0;
    border-bottom: 1px solid ${({ theme }) => theme.border};
  }
`;

export const DayPanelWeekday = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};
`;

export const DayPanelNumber = styled.div`
  font-size: 46px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.02em;
  color: ${({ theme, $today }) => ($today ? "var(--accent)" : theme.textPrimary)};
`;

export const DayPanelMonth = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};

  /* Só a primeira letra maiúscula ("Julho de 2026"), não cada palavra. */
  &::first-letter {
    text-transform: uppercase;
  }
`;

export const DayHojeBadge = styled.span`
  align-self: flex-start;
  margin-top: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: var(--accent-soft);
  color: var(--accent);
`;

export const DayPanelSpacer = styled.div`
  flex: 1 1 auto;
  min-height: 16px;
`;

export const DayStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

export const DayStatRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;

  span {
    color: ${({ theme }) => theme.textSecondary};
  }
  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const DayEventsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px 18px;
  overflow-y: auto;
`;

export const DiaGrupo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const DiaGrupoNome = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};

  .cont {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0;
    color: ${({ theme }) => theme.textTertiary};
  }
`;

export const DayEventCard = styled.button`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border-left: 4px solid ${tokens.border};
    `;
  }}

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border-top: 0;
  border-right: 0;
  border-bottom: 0;
  text-align: left;
  font: inherit;
  cursor: pointer;
  transition: box-shadow 0.14s ease;

  /* Previsao (ambiente ainda sem lote): contorno tracejado e sem
     preenchimento — o tom continua dizendo o prazo; o tracejado diz que
     ainda nao ha lote. */
  ${({ theme, $tone = "default", $previsao }) =>
    $previsao &&
    css`
      background: transparent;
      border: 1px dashed ${getToneTokens(theme, $tone).border};
      border-left-width: 4px;
    `}

  &:hover {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
`;

export const DayCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  strong {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.25;
    color: ${({ theme }) => theme.textPrimary};
  }
  span {
    font-size: 11.5px;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const DayCardPill = styled.span`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

/* ============================================================
 *  Ano
 * ============================================================ */

export const YearGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  padding: 16px;
  background: ${({ theme }) => theme.surface};

  @media (max-width: 1000px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const MiniMonth = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  padding: 10px 10px 12px;
  background: ${({ theme }) => theme.surface};
`;

export const MiniMonthTitle = styled.button`
  align-self: flex-start;
  border: 0;
  background: transparent;
  padding: 2px 4px;
  margin-bottom: 2px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme, $current }) => ($current ? theme.accent : theme.textPrimary)};
  cursor: pointer;
  border-radius: 4px;
  text-transform: capitalize;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const MiniWeekdayRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

export const MiniWeekdayCell = styled.div`
  text-align: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
  letter-spacing: 0.04em;
`;

export const MiniDaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

export const MiniDay = styled.button`
  position: relative;
  border: 0;
  background: transparent;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  padding: 0;
  height: 22px;
  width: 100%;
  border-radius: 999px;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  /* Feriado: sublinhado pontilhado — marca o dia sem competir com a cor,
     que aqui comunica o estado dos eventos. */
  text-decoration: ${({ $feriado }) => ($feriado ? "underline dotted" : "none")};
  text-underline-offset: 3px;
  color: ${({ theme, $outside, $today }) =>
    $today ? "var(--ink-on-accent)" : $outside ? theme.textSecondary : theme.textPrimary};
  opacity: ${({ $outside, $today }) => ($outside && !$today ? 0.4 : 1)};

  ${({ $today, theme }) =>
    $today &&
    css`
      background: ${theme.accent};
      font-weight: 700;
    `}

  /* Mockup: dia com entrega recebe o fundo/tom do estado (verde/amarelo/
     vermelho), não apenas um ponto. */
  ${({ $hasEvents, $today, $tone = "default", theme }) => {
    if (!$hasEvents || $today) return "";
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      font-weight: 700;
    `;
  }}

  /* Dia que so tem previsoes (ambientes sem lote): contorno tracejado no
     tom do prazo, sem preenchimento — mesma linguagem dos chips. */
  ${({ $previsao, $hasEvents, $today, $tone = "default", theme }) => {
    if (!$previsao || !$hasEvents || $today) return "";
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: transparent;
      border: 1px dashed ${tokens.border};
    `;
  }}

  &:hover {
    background: ${({ theme, $today }) => ($today ? theme.accent : theme.border)};
  }
`;

export const MiniDayDot = styled.span`
  position: absolute;
  bottom: 1px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ theme, $tone = "danger" }) => getToneTokens(theme, $tone).border};
`;

/* ============================================================
 *  Popover (compartilhado)
 * ============================================================ */

export const PopoverOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(10, 15, 25, 0.34);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

export const PopoverCard = styled(motion.div)`
  width: min(420px, 96vw);
  max-height: min(80vh, 560px);
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);
  overflow: hidden;
`;

export const PopoverHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

export const PopoverTitle = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  line-height: 1.25;
  text-transform: capitalize;
`;

export const PopoverClose = styled.button`
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const PopoverList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px 12px;
  overflow: auto;
`;
